import React, { useState, useEffect, useCallback, useRef } from "react";
import { Layout, Button, notification, DatePicker, Space, message, Select, Card } from "antd";
import { Row, Form, Col } from "antd";
import { DownloadOutlined, FilterOutlined, ClearOutlined } from "@ant-design/icons";
import * as XLSX from 'xlsx'; 

import TopHeader from "../common/TopHeader/TopHeader";
import Sidebar from "../common/Sidebar/Sidebar";
// import FooterDashboard from "../common/FooterDashboard/FooterDashboard";
import dayjs from "dayjs";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ReportsApi from "../common/Apis/ReportsApi";
import Pagination from "../common/Pagination/Pagination";
import "../common/Pagination/Pagination.css";

const CleaningTraceabilityReport = () => {
    const { Content } = Layout;
    const [formAdd] = Form.useForm();
    const [reportData, setReportData] = useState([]);
    const [filteredData, setFilteredData] = useState([]); // For client-side filtering
    const [isLoading, setIsLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [filterLoading, setFilterLoading] = useState(false);
    
    // Filter states
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [selectedArea, setSelectedArea] = useState(null);
    const [selectedFrequency, setSelectedFrequency] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);
    
    // Department and Area options
    const [departmentOptions, setDepartmentOptions] = useState([]);
    const [areaOptions, setAreaOptions] = useState([]);
    const [frequencyOptions, setFrequencyOptions] = useState([]);
    const [statusOptions, setStatusOptions] = useState([]);
    
    // Pagination state using your custom pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const PageLoading = useCallback(() => {
        setIsLoading(true);
        setTimeout(() => {
            setIsLoading(false);
        }, 800);
    }, []);

    // Extract unique departments from report data
    const extractDepartmentOptions = (data) => {
        const departments = new Set();
        data.forEach(item => {
            if (item.institutionDTO?.code) {
                departments.add(item.institutionDTO.code);
            }
        });
        return Array.from(departments).sort().map(dept => ({
            value: dept,
            label: dept
        }));
    };

    // Extract unique areas from report data based on selected department
    const extractAreaOptions = (data, department) => {
        const areas = new Set();
        data.forEach(item => {
            if (department) {
                if (item.institutionDTO?.code === department && item.taskMasterDTO?.areaDTO?.areaName) {
                    areas.add(item.taskMasterDTO.areaDTO.areaName);
                }
            } else {
                if (item.taskMasterDTO?.areaDTO?.areaName) {
                    areas.add(item.taskMasterDTO.areaDTO.areaName);
                }
            }
        });
        return Array.from(areas).sort().map(area => ({
            value: area,
            label: area
        }));
    };

    // Extract unique frequencies from report data
    const extractFrequencyOptions = (data) => {
        const frequencies = new Set();
        data.forEach(item => {
            if (item.taskMasterDTO?.frequency) {
                frequencies.add(item.taskMasterDTO.frequency);
            }
        });
        return Array.from(frequencies).sort().map(freq => ({
            value: freq,
            label: freq.charAt(0).toUpperCase() + freq.slice(1).toLowerCase()
        }));
    };

    // Extract status options
    const extractStatusOptions = () => {
        return [
            { value: 'completed', label: 'Completed' },
            { value: 'pending', label: 'Pending' }
        ];
    };

    // Apply all filters (department, area, frequency, status)
    const applyFilters = useCallback((data, department, area, frequency, status) => {
        let filtered = [...data];
        
        // Filter by department
        if (department) {
            filtered = filtered.filter(item => 
                item.institutionDTO?.code === department
            );
        }
        
        // Filter by area
        if (area) {
            filtered = filtered.filter(item => 
                item.taskMasterDTO?.areaDTO?.areaName === area
            );
        }
        
        // Filter by frequency
        if (frequency) {
            filtered = filtered.filter(item => 
                item.taskMasterDTO?.frequency === frequency
            );
        }
        
        // Filter by status
        if (status) {
            const isCompleted = status === 'completed';
            filtered = filtered.filter(item => 
                item.isCompleted === isCompleted
            );
        }
        
        return filtered;
    }, []);

    // Update filtered data when filters or report data change
    useEffect(() => {
        if (reportData.length > 0) {
            // Update department options based on all report data
            const deptOptions = extractDepartmentOptions(reportData);
            setDepartmentOptions(deptOptions);
            
            // Update area options based on selected department
            const areaOpts = extractAreaOptions(reportData, selectedDepartment);
            setAreaOptions(areaOpts);
            
            // Update frequency options based on all report data
            const freqOpts = extractFrequencyOptions(reportData);
            setFrequencyOptions(freqOpts);
            
            // Update status options
            const statusOpts = extractStatusOptions();
            setStatusOptions(statusOpts);
            
            // Apply filters
            const filtered = applyFilters(reportData, selectedDepartment, selectedArea, selectedFrequency, selectedStatus);
            setFilteredData(filtered);
            setTotalItems(filtered.length);
            setTotalPages(Math.ceil(filtered.length / pageSize));
            setCurrentPage(1);
        } else {
            setDepartmentOptions([]);
            setAreaOptions([]);
            setFrequencyOptions([]);
            setStatusOptions([]);
            setFilteredData([]);
            setTotalItems(0);
            setTotalPages(0);
        }
    }, [reportData, selectedDepartment, selectedArea, selectedFrequency, selectedStatus, applyFilters, pageSize]);

    const handlePrintButtonClick2 = () => {
        const windowWidth = window.innerWidth * 0.264583;
        const windowHeight = window.innerHeight * 0.264583;

        const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: [windowWidth, windowHeight]
        });

        const fontSize = 10;
        const margin = 5;
        const cellWidthFactor = 0.22;
        const cellHeightFactor = 0.18;
        const thPaddingBottom = 0;

        doc.setFontSize(fontSize);

        const table = document.querySelector('.table');
        const rows = table.querySelectorAll('tr');

        const pageTitle = 'All Tasks Report';
        const pageTitleWidth = doc.getStringUnitWidth(pageTitle) * fontSize / doc.internal.scaleFactor;
        const pageTitleXPos = (doc.internal.pageSize.width - pageTitleWidth) / 2;
        doc.text(pageTitleXPos, margin, pageTitle);

        doc.setFontSize(fontSize);
        const currentDate = new Date().toLocaleString();
        const dateTimeWidth = doc.getStringUnitWidth(currentDate) * fontSize / doc.internal.scaleFactor;
        const dateTimeXPos = doc.internal.pageSize.width - margin - dateTimeWidth;
        doc.text(dateTimeXPos, margin, currentDate);

        let yPos = margin + fontSize;

        rows.forEach((row, rowIndex) => {
            const cells = row.querySelectorAll('th, td');
            let xPos = margin;

            let maxRowspan = 1;
            cells.forEach((cell) => {
                const rowspan = cell.getAttribute('rowspan');
                if (rowspan && parseInt(rowspan) > maxRowspan) {
                    maxRowspan = parseInt(rowspan);
                }
            });

            let rowHeight = row.offsetHeight * cellHeightFactor;
            if (rowIndex === 1) {
                rowHeight *= 1;
            }
            rowHeight *= maxRowspan + 0.5;

            cells.forEach((cell, cellIndex) => {
                const cellText = cell.textContent.trim();
                const cellWidth = cell.offsetWidth * cellWidthFactor;

                const wrappedText = doc.splitTextToSize(cellText, cellWidth - 2);
                const textHeight = wrappedText.length * (fontSize / 2.2);

                if (yPos + textHeight > doc.internal.pageSize.height - margin) {
                    doc.addPage();
                    yPos = margin + fontSize;
                    doc.text(pageTitleXPos, margin, pageTitle);
                    doc.text(dateTimeXPos, margin, currentDate);
                }

                const isHeader = cell.tagName.toLowerCase() === 'th';
                doc.setFont(undefined, isHeader ? 'bold' : 'normal');

                const cellPadding = 0.5;
                let xPosAlign = xPos + cellPadding;

                doc.rect(xPos, yPos, cellWidth, rowHeight);
                doc.text(wrappedText, xPosAlign + 1, yPos + (isHeader ? thPaddingBottom + 5 : 5));

                xPos += cellWidth;
            });

            yPos += rowHeight;
        });

        window.open(doc.output('bloburl'), '_blank');
    };

    // Function to convert date string to Unix timestamp
    function convertToUnixTimestamp(dateString) {
        const parts = dateString.split("/");
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);

        const unixTimestamp = Date.UTC(year, month, day);
        return unixTimestamp;
    }

    // Function to format date from timestamp
    const formatDate = (timestamp) => {
        if (!timestamp) return '-';
        return dayjs(timestamp).format("DD/MM/YYYY");
    };

    // Function to handle export to PDF
    const handleExportReport = () => {
        const dataToExport = filteredData.length > 0 ? filteredData : reportData;
        
        if (!dataToExport || dataToExport.length === 0) {
            message.warning("No data available to export");
            return;
        }

        setExportLoading(true);

        try {
            const startDate = formAdd.getFieldValue('startDate');
            const endDate = formAdd.getFieldValue('endDate');
            const frequency = formAdd.getFieldValue('frequency');

            // Create PDF document
            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4'
            });

            // Add Title
            doc.setFontSize(16);
            doc.text(' All Tasks Report', 14, 15);

            // Add Filter Information
            doc.setFontSize(10);
            let filterInfo = `Date Range: ${startDate ? dayjs(startDate).format('DD/MM/YYYY') : 'N/A'} - ${endDate ? dayjs(endDate).format('DD/MM/YYYY') : 'N/A'}`;
            if (frequency) {
                filterInfo += ` | Frequency: ${frequency}`;
            }
            if (selectedDepartment) {
                filterInfo += ` | Department: ${selectedDepartment}`;
            }
            if (selectedArea) {
                filterInfo += ` | Area: ${selectedArea}`;
            }
            if (selectedFrequency) {
                filterInfo += ` | Filter Frequency: ${selectedFrequency}`;
            }
            if (selectedStatus) {
                filterInfo += ` | Status: ${selectedStatus === 'completed' ? 'Completed' : 'Pending'}`;
            }
            filterInfo += ` | Total Records: ${dataToExport.length}`;
            doc.text(filterInfo, 14, 22);

            // Prepare table data
            const tableData = dataToExport.map((item) => [
                formatDate(item.scheduledDate),
                item.taskMasterDTO?.taskName || '-',
                item.institutionDTO?.code || '-',
                item.taskMasterDTO?.areaDTO?.areaName || '-',
                item.taskMasterDTO?.frequency || '-',
                item.isCompleted ? 'Completed' : 'Pending', 
                item.completedBy || '-',
                item?.managerNote || '-',
            ]);

            // Generate table using autoTable
            autoTable(doc, {
                startY: 28,
                head: [[
                    'Date',
                    'Task Name',
                    'Department',
                    'Area',
                    'Frequency',
                    'Status',
                    'Completed By',
                    'Manager Note'
                ]],
                body: tableData,
                styles: {
                    fontSize: 8,
                    cellPadding: 2,
                    overflow: 'linebreak'
                },
                headStyles: {
                    fillColor: [79, 129, 189],
                    textColor: [255, 255, 255],
                    fontStyle: 'bold',
                    fontSize: 9
                },
                alternateRowStyles: {
                    fillColor: [245, 245, 245]
                },
                margin: {
                    left: 8,
                    right: 8,
                    top: 30
                },
                columnStyles: {
                    0: { cellWidth: 25 },
                    1: { cellWidth: 35 },
                    2: { cellWidth: 30 },
                    3: { cellWidth: 30 },
                    4: { cellWidth: 25 },
                    5: { cellWidth: 25 },
                    6: { cellWidth: 30 }
                },
                didDrawPage: function(data) {
                    const pageCount = doc.internal.getNumberOfPages();
                    doc.setFontSize(8);
                    doc.text(
                        `Page ${data.pageNumber} of ${pageCount}`,
                        doc.internal.pageSize.width - 20,
                        doc.internal.pageSize.height - 10
                    );
                    
                    doc.text(
                        `Generated: ${dayjs().format('DD/MM/YYYY HH:mm:ss')}`,
                        14,
                        doc.internal.pageSize.height - 10
                    );
                }
            });

            // Generate filename
            let fileName = `Cleaning_Traceability_Report_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.pdf`;

            if (startDate && endDate) {
                const startStr = dayjs(startDate).format('YYYY-MM-DD');
                const endStr = dayjs(endDate).format('YYYY-MM-DD');
                const freqStr = frequency ? `_${frequency}` : '';
                const deptStr = selectedDepartment ? `_${selectedDepartment}` : '';
                const areaStr = selectedArea ? `_${selectedArea}` : '';
                const statusStr = selectedStatus ? `_${selectedStatus}` : '';
                fileName = `Cleaning_Traceability_Report_${startStr}_to_${endStr}${freqStr}${deptStr}${areaStr}${statusStr}.pdf`;
            }

            // Save the PDF
            doc.save(fileName);
            message.success(`Successfully exported ${dataToExport.length} records to PDF!`);
        } catch (error) {
            console.error("Error exporting report:", error);
            message.error("Failed to export report");
        } finally {
            setExportLoading(false);
        }
    };

    const getCurrent = useCallback(async () => {
    const todayDate = dayjs();
    const tomorrowDate = dayjs().add(1, 'day');

        formAdd.setFieldsValue({
            startDate: todayDate,
            endDate: tomorrowDate,
            frequency: "DAILY"
        });

        // Reset filters
        setSelectedDepartment(null);
        setSelectedArea(null);
        setSelectedFrequency(null);
        setSelectedStatus(null);

        try {
            setFilterLoading(true);

            const startTimestamp = convertToUnixTimestamp(
                todayDate.format("DD/MM/YYYY")
            );

            const endTimestamp =
                convertToUnixTimestamp(
                    tomorrowDate.format("DD/MM/YYYY")
                ) + (24 * 60 * 60 * 1000);

            const response = await ReportsApi.CleaningTraceability(
                startTimestamp,
                endTimestamp,
                "DAILY"
            );

            let reportDataArray = [];

            if (response?.payload) {
                reportDataArray = response.payload;
            } else if (response?.data) {
                reportDataArray = response.data;
            } else if (Array.isArray(response)) {
                reportDataArray = response;
            }

            setReportData(reportDataArray);

        } catch (error) {
            console.error("Error fetching report:", error);
            message.error(error?.message || "Failed to fetch report data");

            setReportData([]);
            setFilteredData([]);
        } finally {
            setFilterLoading(false);
        }
    }, [formAdd]);

    useEffect(() => {
        PageLoading();
        getCurrent();
    }, [PageLoading, getCurrent]);

    // Function to handle filter/submit
    const handleSubmit = async (data) => {
        const startDate = formAdd.getFieldValue('startDate');
        const endDate = formAdd.getFieldValue('endDate');
        const frequency = formAdd.getFieldValue('frequency');
        
        if (!startDate || !endDate) {
            message.warning("Please select both start date and end date");
            return;
        }
        
        if (!frequency) {
            message.warning("Please select frequency");
            return;
        }
        
        setFilterLoading(true);
        try {
            const startDateStr = dayjs(startDate).format("DD/MM/YYYY");
            const endDateStr = dayjs(endDate).format("DD/MM/YYYY");
            const startTimestamp = convertToUnixTimestamp(startDateStr);
            const endTimestamp = convertToUnixTimestamp(endDateStr) + (24 * 60 * 60 * 1000); 
            
            // Call the API to get report data
            const response = await ReportsApi.CleaningTraceability(startTimestamp, endTimestamp, frequency);
            
            // Extract payload from response
            let reportDataArray = [];
            if (response && response.payload) {
                reportDataArray = response.payload;
            } else if (response && response.data) {
                reportDataArray = response.data;
            } else if (Array.isArray(response)) {
                reportDataArray = response;
            } else {
                reportDataArray = [];
            }
            
            setReportData(reportDataArray);
            // Reset all filters when new data is loaded
            setSelectedDepartment(null);
            setSelectedArea(null);
            setSelectedFrequency(null);
            setSelectedStatus(null);
            // Filtered data will be set by useEffect
            message.success(`Report loaded successfully! Found ${reportDataArray.length} records`);
        } catch (error) {
            console.error("Error fetching report:", error);
            message.error(error?.message || "Failed to fetch report data");
            setReportData([]);
            setFilteredData([]);
            setTotalItems(0);
            setTotalPages(0);
            setDepartmentOptions([]);
            setAreaOptions([]);
            setFrequencyOptions([]);
            setStatusOptions([]);
        } finally {
            setFilterLoading(false);
        }
    };

    // Reset all filters
    const resetAllFilters = () => {
        setSelectedDepartment(null);
        setSelectedArea(null);
        setSelectedFrequency(null);
        setSelectedStatus(null);
        setFilteredData(reportData);
        message.info("All filters have been reset");
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleClear = () => {
        setIsLoading(true);
        getCurrent();
        setReportData([]);
        setFilteredData([]);
        setTotalItems(0);
        setTotalPages(0);
        setCurrentPage(1);
        setDepartmentOptions([]);
        setAreaOptions([]);
        setFrequencyOptions([]);
        setStatusOptions([]);
        setSelectedDepartment(null);
        setSelectedArea(null);
        setSelectedFrequency(null);
        setSelectedStatus(null);
        setTimeout(() => {
            setIsLoading(false);
        }, 800);
    };

    // Get current page data for client-side pagination
    const getCurrentPageData = () => {
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return filteredData.slice(startIndex, endIndex);
    };

    const currentPageData = getCurrentPageData();

    // Frequency options for the main filter
    const mainFrequencyOptions = [
        { value: "DAILY", label: "Daily" },
        { value: "WEEKLY", label: "Weekly" },
        { value: "MONTHLY", label: "Monthly" },
    ];

    // Check if any advanced filter is applied
    const hasActiveFilters = selectedDepartment || selectedArea || selectedFrequency || selectedStatus;

    return (
        <Layout style={{ minHeight: "100vh" }}>
            <Sidebar />
            <Layout>
                <TopHeader onPrintResponse2={handlePrintButtonClick2} />
                {isLoading && (
                    <div className="spinner-overlay" id="spinnerOverlay">
                        <div className="spinner"></div>
                    </div>
                )}

                <Content
                    className="form-content nav-content"
                    style={{
                        margin: "14px 16px 0",
                        padding: "30px",
                        background: "white",
                    }}
                >
                    <div>
                        {/* Filter Section - Separated Card */}
                        <Card 
                            style={{ 
                                marginBottom: '24px',
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                            }}
                            bodyStyle={{ padding: '20px' }}
                        >
                            <div style={{ marginBottom: '16px', borderBottom: '1px solid #f0f0f0', paddingBottom: '10px' }}>
                                <Space>
                                    <FilterOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
                                    <span style={{ fontSize: '16px', fontWeight: 500, color: '#333' }}>Filter Options</span>
                                </Space>
                            </div>
                            
                            <Form
                                form={formAdd}
                                name="cleaning_filter_form"
                                layout="vertical"
                                onFinish={handleSubmit}
                            >
                                <Row gutter={[16, 0]}>
                                    <Col lg={6} md={12} sm={24} xs={24}>
                                        <Form.Item
                                            name="startDate"
                                            label="Start Date"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: "Please Select Start Date.",
                                                },
                                            ]}
                                        >
                                            <DatePicker
                                                format={"DD/MM/YYYY"}
                                                placeholder="Select start date"
                                                style={{ width: '100%', height: '40px' }}
                                                allowClear={false}
                                                suffixIcon={null}
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col lg={6} md={12} sm={24} xs={24}>
                                        <Form.Item
                                            name="endDate"
                                            label="End Date"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: "Please Select End Date.",
                                                },
                                            ]}
                                        >
                                            <DatePicker
                                                format={"DD/MM/YYYY"}
                                                placeholder="Select end date"
                                                style={{ width: '100%', height: '40px' }}
                                                allowClear={false}
                                                suffixIcon={null}
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col lg={6} md={12} sm={24} xs={24}>
                                        <Form.Item
                                            name="frequency"
                                            label="Frequency"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: "Please Select Frequency.",
                                                },
                                            ]}
                                        >
                                            <Select
                                                placeholder="Select Frequency"
                                                options={mainFrequencyOptions}
                                                style={{ width: '100%', height: '40px' }}
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col lg={6} md={24} sm={24} xs={24}>
                                        <Form.Item label="Actions">
                                            <Space size="middle" wrap>
                                                <Button
                                                    type="primary"
                                                    htmlType="submit"
                                                    loading={filterLoading}
                                                    icon={<FilterOutlined />}
                                                    style={{ height: '40px', width: '120px' }}
                                                >
                                                    Apply Filter
                                                </Button>
                                                <Button
                                                    onClick={() => handleClear()}
                                                    icon={<ClearOutlined />}
                                                    style={{ height: '40px', width: '100px' }}
                                                >
                                                    Clear
                                                </Button>
                                            </Space>
                                        </Form.Item>
                                    </Col>
                                </Row>
                                
                                {/* Advanced Filters Row */}
                                    <Row gutter={[16, 0]} style={{ marginTop: '16px', borderTop: '1px dashed #e8e8e8', paddingTop: '16px' }}>
                                        <Col lg={6} md={12} sm={24} xs={24}>
                                            <Form.Item label="Department">
                                                <Select
                                                    placeholder="All Departments"
                                                    options={departmentOptions}
                                                    value={selectedDepartment}
                                                    onChange={(value) => {
                                                        setSelectedDepartment(value);
                                                        setSelectedArea(null); // Reset area when department changes
                                                    }}
                                                    style={{ width: '100%', height: '40px' }}
                                                    allowClear
                                                    showSearch
                                                    filterOption={(input, option) =>
                                                        option?.label?.toLowerCase().includes(input.toLowerCase())
                                                    }
                                                />
                                            </Form.Item>
                                        </Col>

                                        <Col lg={6} md={12} sm={24} xs={24}>
                                            <Form.Item label="Area">
                                                <Select
                                                    placeholder={selectedDepartment ? "All Areas" : "Select Department First"}
                                                    options={areaOptions}
                                                    value={selectedArea}
                                                    onChange={(value) => setSelectedArea(value)}
                                                    style={{ width: '100%', height: '40px' }}
                                                    disabled={!selectedDepartment && areaOptions.length === 0}
                                                    allowClear
                                                    showSearch
                                                    filterOption={(input, option) =>
                                                        option?.label?.toLowerCase().includes(input.toLowerCase())
                                                    }
                                                />
                                            </Form.Item>
                                        </Col>

                                        <Col lg={6} md={12} sm={24} xs={24}>
                                            <Form.Item label="Frequency">
                                                <Select
                                                    placeholder="All Frequencies"
                                                    options={frequencyOptions}
                                                    value={selectedFrequency}
                                                    onChange={(value) => setSelectedFrequency(value)}
                                                    style={{ width: '100%', height: '40px' }}
                                                    allowClear
                                                    showSearch
                                                    filterOption={(input, option) =>
                                                        option?.label?.toLowerCase().includes(input.toLowerCase())
                                                    }
                                                />
                                            </Form.Item>
                                        </Col>

                                        <Col lg={6} md={12} sm={24} xs={24}>
                                            <Form.Item label="Status">
                                                <Select
                                                    placeholder="All Status"
                                                    options={statusOptions}
                                                    value={selectedStatus}
                                                    onChange={(value) => setSelectedStatus(value)}
                                                    style={{ width: '100%', height: '40px' }}
                                                    allowClear
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                               
                            </Form>
                            
                            {/* Filter Summary */}
                            {reportData.length > 0 && (
                                <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                                    <div style={{ padding: '8px 12px', background: '#e6f7ff', borderRadius: '4px', fontSize: '12px', color: '#1890ff' }}>
                                        <strong>Total Records:</strong> {reportData.length} | 
                                        <strong> Displaying:</strong> {filteredData.length} records
                                        {hasActiveFilters && (
                                            <span style={{ marginLeft: '8px' }}>
                                                (Filtered by: 
                                                {selectedDepartment && ` Department: ${selectedDepartment}`}
                                                {selectedArea && `, Area: ${selectedArea}`}
                                                {selectedFrequency && `, Frequency: ${selectedFrequency}`}
                                                {selectedStatus && `, Status: ${selectedStatus === 'completed' ? 'Completed' : 'Pending'}`}
                                                )
                                            </span>
                                        )}
                                    </div>
                                    <Button
                                        onClick={resetAllFilters}
                                        icon={<ClearOutlined />}
                                        size="small"
                                        disabled={!hasActiveFilters}
                                    >
                                        Reset All Filters
                                    </Button>
                                </div>
                            )}
                        </Card>

                        {/* Report Results Section */}
                        <Card 
                            style={{ 
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                            }}
                            bodyStyle={{ padding: '20px' }}
                        >
                            <div style={{ 
                                marginBottom: '16px', 
                                borderBottom: '1px solid #f0f0f0', 
                                paddingBottom: '10px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: '10px'
                            }}>
                                <Space>
                                    <span style={{ fontSize: '16px', fontWeight: 500, color: '#333' }}> All Tasks Report Results</span>
                                    <span style={{ fontSize: '12px', color: '#999' }}>
                                        {filteredData.length > 0 && `(${filteredData.length} records found)`}
                                        {filteredData.length !== reportData.length && reportData.length > 0 && 
                                            ` (filtered from ${reportData.length} total)`
                                        }
                                    </span>
                                </Space>
                                <Button
                                    type="primary"
                                    onClick={handleExportReport}
                                    loading={exportLoading}
                                    icon={<DownloadOutlined />}
                                    style={{ 
                                        height: '36px',
                                        backgroundColor: '#52c41a',
                                        borderColor: '#52c41a'
                                    }}
                                >
                                    Export Cleaning Report PDF
                                </Button>
                            </div>
                            
                            <div className="dateFilterTable" style={{ overflowX: 'auto' }}>
                                <table className="table users-table Reportable" style={{ width: '100%', minWidth: '800px' }}>
                                    <thead>
                                        <tr>
                                            <th colSpan="7" className="text-center" style={{ fontSize: '16px', padding: '12px' }}>
                                                All Tasks Report
                                            </th>
                                        </tr>
                                    </thead>
                                    <thead>
                                        <tr>
                                            <th style={{ padding: '12px', textAlign: 'center' }}>Date</th>
                                            <th style={{ padding: '12px', textAlign: 'center' }}>Task Name</th>
                                            <th style={{ padding: '12px', textAlign: 'center' }}>Department</th>
                                            <th style={{ padding: '12px', textAlign: 'center' }}>Area</th>
                                            <th style={{ padding: '12px', textAlign: 'center' }}>Frequency</th>
                                            <th style={{ padding: '12px', textAlign: 'center' }}>Status</th>
                                            <th style={{ padding: '12px', textAlign: 'center' }}>Completed By</th>
                                            <th style={{ padding: '12px', textAlign: 'center' }}>Manager Note</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentPageData && currentPageData.length > 0 ? (
                                            currentPageData.map((item, index) => (
                                                <tr key={item.id || index}>
                                                    <td style={{ padding: '10px', textAlign: 'center' }}>{formatDate(item.scheduledDate)}</td>
                                                    <td style={{ padding: '10px', textAlign: 'left' }}>{item.taskMasterDTO?.taskName || '-'}</td>
                                                    <td style={{ padding: '10px', textAlign: 'center' }}>{item.institutionDTO?.code || '-'}</td>
                                                    <td style={{ padding: '10px', textAlign: 'center' }}>{item.taskMasterDTO?.areaDTO?.areaName || '-'}</td>
                                                    <td style={{ padding: '10px', textAlign: 'center' }}>{item.taskMasterDTO?.frequency || '-'}</td>
                                                    <td style={{ padding: '10px', textAlign: 'center' }}>
                                                        <span style={{
                                                            color: item.isCompleted ? '#52c41a' : '#faad14',
                                                            fontWeight: '500'
                                                        }}>
                                                            {item.isCompleted ? 'Completed' : 'Pending'}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '10px', textAlign: 'center' }}>{item.completedBy || '-'}</td>
                                                    <td style={{ padding: '10px', textAlign: 'center' }}>{item.managerNote || '-'}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr className="text-center EmptyTable">
                                                <td colSpan={7} className="text-center" style={{ padding: '40px' }}>
                                                    <img
                                                        src="/assest/Images/emptyIcon.svg"
                                                        className="logo-png mt-3"
                                                        style={{ width: "10%" }}
                                                        alt="No data"
                                                    />
                                                    <br />
                                                    {filterLoading ? "Loading..." : 
                                                     reportData.length > 0 ? "No data matches the selected filters" :
                                                     "Select date range and frequency to generate report"}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            
                            {/* Custom Pagination */}
                            {filteredData.length > 0 && (
                                <div style={{ marginTop: '20px' }}>
                                    <Pagination
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        onPageChange={handlePageChange}
                                        maxVisiblePages={5}
                                        showPageInfo={true}
                                        totalItems={totalItems}
                                        pageSize={pageSize}
                                    />
                                </div>
                            )}
                        </Card>
                    </div>
                </Content>

                {/* <FooterDashboard /> */}
            </Layout>
        </Layout>
    );
};

export default CleaningTraceabilityReport;